import { Link } from 'react-router';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { useSEO, mergeKeywordSets, COMPETITIVE_KEYWORD_CLUSTERS } from '../lib/seo';
import { StockHeroStrip, StockImage, WarmMarketingBackdrop } from '../components/marketing/MarketingVisuals';

export default function BestCouplesApp() {
  useSEO({
    title: 'Best Couples App for Better Communication',
    description:
      'Looking for the best couples app? Compare what matters most: communication quality, conflict de-escalation support, and daily usability.',
    keywords: mergeKeywordSets(
      COMPETITIVE_KEYWORD_CLUSTERS.core as unknown as string[],
      COMPETITIVE_KEYWORD_CLUSTERS.communication as unknown as string[],
      ['best couples app', 'couples communication app', 'apps for couples'],
    ),
    canonical: '/best-couples-app',
    ogType: 'website',
    twitterCard: 'summary_large_image',
  });

  return (
    <WarmMarketingBackdrop>
      <div className="mx-auto max-w-5xl space-y-10 px-4 py-10 md:py-14">
        <header className="grid gap-6 overflow-hidden rounded-3xl border border-violet-200/60 bg-white/90 shadow-xl md:grid-cols-[1.15fr_0.85fr] md:items-center">
          <div className="p-6 md:p-10">
            <h1 className="text-3xl font-black tracking-tight text-neutral-900 md:text-4xl">
              Best couples app for communication and repair
            </h1>
            <p className="mt-3 max-w-2xl text-base leading-relaxed text-neutral-600 md:text-lg">
              The best couples app should help both partners communicate clearly, de-escalate faster, and build
              consistent relationship habits.
            </p>
          </div>
          <StockImage
            stockKey="coupleHomeRelax"
            width={640}
            height={720}
            priority
            alt="Couple having a calm conversation at home"
            className="h-56 w-full object-cover md:h-full md:min-h-[280px] md:rounded-none md:rounded-r-3xl"
          />
        </header>

        <StockHeroStrip stockKey="handsWalking" alt="Couple walking together, holding hands" />

        <div className="grid gap-4 md:grid-cols-2">
          <Card className="border-violet-100/90 bg-white/95 p-6 shadow-md">
            <h2 className="text-lg font-semibold text-neutral-900">What to prioritize</h2>
            <ul className="mt-3 list-disc space-y-2 pl-5 text-sm text-neutral-700">
              <li>Conversation support you can use in real moments.</li>
              <li>Clear conflict repair workflows instead of generic tips.</li>
              <li>Consistent day-to-day experience for both partners.</li>
            </ul>
          </Card>
          <Card className="border-violet-100/90 bg-white/95 p-6 shadow-md">
            <h2 className="text-lg font-semibold text-neutral-900">How TENSION fits</h2>
            <ul className="mt-3 list-disc space-y-2 pl-5 text-sm text-neutral-700">
              <li>Relationship Intelligence for trend visibility.</li>
              <li>Conversation Micro-Coach for safer messages.</li>
              <li>Guided resources for communication and conflict repair.</li>
            </ul>
          </Card>
        </div>

        <Card className="border-violet-200/80 bg-gradient-to-r from-violet-50/95 to-fuchsia-50/90 p-8 shadow-lg">
          <h2 className="text-xl font-semibold text-neutral-900">Compare before you commit</h2>
          <div className="mt-5 flex flex-wrap gap-3">
            <Link to="/compare/tension-vs-paired">
              <Button className="bg-gradient-to-r from-violet-600 to-fuchsia-600 text-white shadow-md">
                View side-by-side comparison
              </Button>
            </Link>
            <Link to="/alternatives/paired">
              <Button variant="outline" className="border-violet-300 bg-white/90">
                Explore alternatives
              </Button>
            </Link>
            <Link to="/pricing">
              <Button variant="outline" className="border-violet-300 bg-white/90">
                See pricing
              </Button>
            </Link>
          </div>
        </Card>
      </div>
    </WarmMarketingBackdrop>
  );
}
